import React, { useState, useEffect } from 'react';
import './CodeListModal.css';
import { getCodeList, CodeDocument, ApiResponse } from '../../services/SaveService';

interface CodeListModalProps {
  onClose: () => void;
  onSelectFile: (fileId: string) => void;
}

const CodeListModal: React.FC<CodeListModalProps> = ({ onClose, onSelectFile }) => {
  const [documents, setDocuments] = useState<CodeDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<CodeDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  // 每页显示6条数据
  const PAGE_SIZE = 6;
  
  // 从 API 获取文档列表
  const fetchDocuments = async (page: number = 1, keyword?: string) => {
    console.log('开始获取文档列表...');
    setIsLoading(true);
    try {
      // 使用 SaveService 中的 getCodeList 函数获取代码列表
      const response = await getCodeList(page, keyword, PAGE_SIZE);
      
      console.log('API响应数据:', response);
      
      if (response && response.success) {
        const processedDocs = response.data.docs || [];
        
        console.log('处理后的文档数据:', processedDocs);
        
        setDocuments(processedDocs);
        setFilteredDocuments(processedDocs);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setCurrentPage(response.data.pagination?.page || 1);
      } else {
        console.error('获取代码列表失败:', response?.message || '未知错误');
        // 可以在这里添加错误提示
      }
    } catch (error) {
      console.error('获取文档列表失败:', error);
      // 清空数据，防止显示旧数据
      setDocuments([]);
      setFilteredDocuments([]);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      // 确保无论成功或失败都会关闭加载状态
      console.log('结束加载状态');
      setIsLoading(false);
    }
  };

  // 处理搜索输入变化 - 只在本地过滤，不触发API请求
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // 如果搜索框为空，重新加载第一页数据
    if (query.trim() === '') {
      fetchDocuments(1);
    }
  };

  // 处理搜索按钮点击 - 使用搜索API
  const handleSearchClick = () => {
    if (searchQuery.trim() === '') {
      // 如果搜索框为空，加载第一页数据
      fetchDocuments(1);
    } else {
      // 否则使用搜索API
      fetchDocuments(1, searchQuery);
    }
  };

  // 处理翻页
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchDocuments(newPage, searchQuery.trim() !== '' ? searchQuery : undefined);
      // 滚动到顶部
      const modalContent = document.querySelector('.code-list-content');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
    }
  };

  // 格式化时间戳
  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  // 组件加载时获取文档列表
  useEffect(() => {
    fetchDocuments();
  }, []);

  return (
    <div className="code-list-modal-overlay">
      <div className="code-list-modal">
        <div className="code-list-header">
          <h2>代码文件列表</h2>
          <button 
            className="close-button"
            onClick={onClose}
            title="关闭"
          >
            ×
          </button>
        </div>
        
        <div className="code-list-search">
          <input
            type="text"
            placeholder="搜索文件..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
            onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
          />
          <button 
            className="search-button"
            onClick={handleSearchClick}
            title="搜索文件"
          >
            搜索
          </button>
        </div>
        
        <div className="code-list-content">
          {isLoading ? (
            <div className="loading-indicator">加载中...</div>
          ) : filteredDocuments.length > 0 ? (
            <>
              <div className="code-file-list-container">
                <ul className="code-file-list">
                  {filteredDocuments.map(doc => (
                    <li 
                      key={doc.doc_id}
                      className="code-file-item"
                      onClick={() => onSelectFile(doc.doc_id)}
                    >
                      <div className="file-info">
                        <span className="file-title" title={doc.title}>
                          {doc.title.length > 30 ? `${doc.title.substring(0, 30)}...` : doc.title}
                        </span>
                        <div className="file-meta">
                          {doc.language && (
                            <span className="file-language">
                              {doc.language === 'cpp' ? 'C++' : 'Python'}
                            </span>
                          )}
                          <span className="file-version">版本: {doc.version}</span>
                          {doc.previewContent && (
                            <span className="file-preview" title={doc.content}>
                              {doc.previewContent}
                            </span>
                          )}
                          <span className="file-date">{formatTimestamp(doc.timestamp)}</span>
                        </div>
                      </div>
                      <span className="file-arrow">→</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="pagination">
                <div className="pagination-info">
                  共 {documents.length} 条，第 {currentPage}/{totalPages} 页
                </div>
                <div className="pagination-controls">
                  {totalPages > 1 && (
                    <>
                      <button 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        title="第一页"
                      >
                        «
                      </button>
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        title="上一页"
                      >
                        ‹
                      </button>
                      {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                        // 显示当前页附近的页码，减少显示数量以适应移动设备
                        let pageNum: number;
                        if (totalPages <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage <= 2) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 1) {
                          pageNum = totalPages - 2 + i;
                        } else {
                          pageNum = currentPage - 1 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
                            aria-current={currentPage === pageNum ? 'page' : undefined}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-button"
                        title="下一页"
                      >
                        ›
                      </button>
                      <button 
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-button"
                        title="最后一页"
                      >
                        »
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-results">没有找到匹配的文件</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeListModal;
